import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Settings as SettingsIcon, Save, AlertCircle, Database, Trash2 } from 'lucide-react';

interface SystemSetting {
  value: string;
  description: string | null;
  updated_at: string | null;
}

type SettingsData = Record<string, SystemSetting>;

export function Settings() {
  const queryClient = useQueryClient();
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState('');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => {
      const res = await api.get<SettingsData>('/admin/settings');
      if (res.success) return res.data;
      throw new Error(res.error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const res = await api.put(`/admin/settings/${key}`, { value });
      if (!res.success) throw new Error(res.error || 'Failed to update setting');
      return res;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setSuccessMessage('Setting saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    },
  });

  const seedDataMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        toolrooms: number;
        locations: number;
        categories: number;
        tools: number;
        inventory: number;
      }>('/admin/seed-sample-data', {});
      if (!res.success) throw new Error(res.error || 'Failed to seed data');
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['toolrooms'] });
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['tools'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      setSuccessMessage(
        `✅ Sample data created! ${data?.toolrooms} toolrooms, ${data?.locations} locations, ` +
        `${data?.categories} categories, ${data?.tools} tools, ${data?.inventory} inventory bins`
      );
      setTimeout(() => setSuccessMessage(''), 10000);
    },
    onError: (error) => {
      setSuccessMessage(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setSuccessMessage(''), 8000);
    },
  });

  const clearDatabaseMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{
        cleared: string[];
        errors: string[];
      }>('/admin/clear-database', {});
      if (!res.success) throw new Error(res.error || 'Failed to clear database');
      return res.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries();
      setSuccessMessage(
        `✅ Database cleared! ${data?.cleared.length} tables emptied.`
      );
      setTimeout(() => setSuccessMessage(''), 10000);
    },
    onError: (error) => {
      setSuccessMessage(`❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTimeout(() => setSuccessMessage(''), 8000);
    },
  });

  const handleSave = (key: string) => {
    const value = editedValues[key];
    if (value !== undefined) {
      updateMutation.mutate({ key, value });
      setEditedValues((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const getValue = (key: string) => {
    if (editedValues[key] !== undefined) return editedValues[key];
    return settings?.[key]?.value || '';
  };

  const hasChanges = (key: string) => {
    return editedValues[key] !== undefined && editedValues[key] !== settings?.[key]?.value;
  };

  const settingsConfig = [
    {
      key: 'low_stock_alert_mode',
      label: 'Low Stock Alert Mode',
      description: 'How the dashboard counts low stock items',
      type: 'select' as const,
      options: [
        { value: 'realtime', label: 'Realtime - Count items below min quantity' },
        { value: 'alerts', label: 'Alerts Only - Count unresolved restock alerts' },
      ],
    },
    {
      key: 'company_name',
      label: 'Company Name',
      description: 'Your company or facility name shown in the app',
      type: 'text' as const,
    },
    {
      key: 'default_checkout_duration_hours',
      label: 'Default Checkout Duration (hours)',
      description: 'Default expected return time when checking out tools',
      type: 'number' as const,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold">System Settings</h1>
        <p className="text-foreground-muted">Configure application-wide settings</p>
      </div>

      {successMessage && (
        <div className="p-3 text-sm text-success bg-success/10 border border-success/30 rounded-lg">
          {successMessage}
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <SettingsIcon className="h-5 w-5" />
            General Settings
          </div>
        </CardHeader>
        <CardBody className="space-y-6">
          {settingsConfig.map((config) => (
            <div key={config.key} className="space-y-2">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <label className="block font-medium">{config.label}</label>
                  <p className="text-sm text-foreground-muted">{config.description}</p>
                </div>
                {hasChanges(config.key) && (
                  <Button
                    size="sm"
                    onClick={() => handleSave(config.key)}
                    loading={updateMutation.isPending}
                  >
                    <Save className="h-4 w-4" />
                    Save
                  </Button>
                )}
              </div>
              <div className="max-w-md">
                {config.type === 'select' ? (
                  <Select
                    value={getValue(config.key)}
                    onChange={(e) =>
                      setEditedValues((prev) => ({ ...prev, [config.key]: e.target.value }))
                    }
                    options={config.options || []}
                  />
                ) : (
                  <Input
                    type={config.type === 'number' ? 'number' : 'text'}
                    value={getValue(config.key)}
                    onChange={(e) =>
                      setEditedValues((prev) => ({ ...prev, [config.key]: e.target.value }))
                    }
                    onBlur={() => hasChanges(config.key) && handleSave(config.key)}
                  />
                )}
              </div>
            </div>
          ))}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Sample Data
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="text-sm text-foreground-muted space-y-2">
            <p>
              Create realistic sample data to test and explore the application. This will create:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>5 toolrooms (Main Production, CNC, Assembly, Maintenance, Fabrication)</li>
              <li>25 storage locations across all toolrooms</li>
              <li>6 tool categories (Cutting, Measuring, Hand Tools, Power Tools, Workholding, Abrasives)</li>
              <li>21 tool definitions with realistic part numbers and costs</li>
              <li>Inventory distributed across locations with realistic quantities</li>
            </ul>
            <div className="pt-2">
              <p className="text-warning font-medium">
                Note: This will add new data to your database. It won&apos;t delete existing data.
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              if (confirm('Create sample data? This will add toolrooms, categories, tools, and inventory to your database.')) {
                seedDataMutation.mutate();
              }
            }}
            loading={seedDataMutation.isPending}
          >
            <Database className="h-4 w-4" />
            Create Sample Data
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 text-error">
            <Trash2 className="h-5 w-5" />
            Danger Zone
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="text-sm text-foreground-muted space-y-2">
            <p className="font-semibold text-error">
              Clear Database (Testing Only)
            </p>
            <p>
              This will permanently delete all data from the database including:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>All toolrooms and storage locations</li>
              <li>All tool categories and definitions</li>
              <li>All inventory (tool units and bin inventory)</li>
              <li>All jobs and checkout history</li>
              <li>All audits and notifications</li>
            </ul>
            <div className="pt-2">
              <p className="text-error font-medium">
                ⚠️ WARNING: This action cannot be undone! User accounts will NOT be deleted.
              </p>
            </div>
          </div>
          <Button
            variant="danger"
            onClick={() => {
              const confirmation = prompt(
                'This will permanently delete ALL data from your database!\n\n' +
                'Type "DELETE ALL DATA" to confirm:'
              );
              if (confirmation === 'DELETE ALL DATA') {
                clearDatabaseMutation.mutate();
              }
            }}
            loading={clearDatabaseMutation.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Clear All Database Data
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            About Settings
          </div>
        </CardHeader>
        <CardBody>
          <div className="text-sm text-foreground-muted space-y-2">
            <p>
              <strong>Realtime mode:</strong> The dashboard counts all tools currently below their
              minimum quantity threshold. This gives you an accurate, real-time view of inventory
              that needs attention.
            </p>
            <p>
              <strong>Alerts mode:</strong> The dashboard only counts items that have triggered a
              restock alert that hasn&apos;t been resolved. This is useful if you want to
              acknowledge alerts as they&apos;re addressed, even before stock is replenished.
            </p>
          </div>
        </CardBody>
      </Card>

    </div>
  );
}
